'use client';

import { Link } from '@/components/link';
import { cn } from '@/lib/utils';
import { WINE_VINTAGE_YEAR_ATTRIBUTE_ID } from '@/lib/wine-vintage';
import { type ReactElement, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { onRadioGroupKeyDown } from '@/lib/radio-group-keyboard';

export { WINE_VINTAGE_YEAR_ATTRIBUTE_ID };

export type WineVintageValue = {
    name: string;
    value: string;
    orderable?: boolean;
    href: string;
    selected: boolean;
};

type WineVintageSelectorProps = {
    values: WineVintageValue[];
    /** Variation attribute display name from BM (e.g. "Annata"). */
    label?: string;
    /** Currently selected vintage year (controlled or from URL). */
    selectedValue?: string;
    /** When set, tiles use callbacks instead of URL navigation. */
    onSelect?: (value: string) => void;
    /** When true, only the selected tile is shown and interaction is disabled. */
    readOnly?: boolean;
};

/**
 * Wine vintage year tiles (Figma Annata selector).
 * Shows only available vintages; selected year scrolls into view.
 * Uncontrolled: navigates via Link hrefs. Controlled: calls onSelect.
 */
export default function WineVintageSelector({
    values,
    label,
    selectedValue,
    onSelect,
    readOnly = false,
}: WineVintageSelectorProps): ReactElement | null {
    const { t } = useTranslation('product');
    const selectedRef = useRef<HTMLElement | null>(null);
    const displayLabel = label?.trim() || t('wineVintage.label');

    const tiles = (readOnly ? values.filter((value) => value.value === selectedValue) : values).filter((value) => {
        if (readOnly) {
            return true;
        }
        // Story: only available vintages — keep the current selection visible even if OOS.
        return value.orderable !== false || value.value === selectedValue || value.selected;
    });
    // Roving tabindex: only the selected (or first enabled) tile is reachable via Tab.
    const enabledTiles = tiles.filter((tile) => !(readOnly || tile.orderable === false));
    const selectedEnabled = enabledTiles.find((tile) => selectedValue === tile.value || tile.selected);
    const tabStopValue = selectedEnabled?.value ?? enabledTiles[0]?.value;

    useEffect(() => {
        selectedRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
        });
    }, [selectedValue]);

    if (tiles.length === 0) {
        return null;
    }

    return (
        <div className="flex w-full min-w-0 flex-col gap-2.5 order-1" data-testid="wine-vintage-selector">
            <p className="body-copy-m m-0 text-muted-foreground">{displayLabel}:</p>
            <div
                className="flex w-full min-w-0 pb-3 gap-2 overflow-x-auto [scrollbar-width:thin] [scrollbar-color:var(--brown-10)_var(--sand-90)] [&::-webkit-scrollbar]:h-0.5 [&::-webkit-scrollbar-track]:bg-[var(--sand-90)] [&::-webkit-scrollbar-thumb]:bg-[var(--brown-10)] [&::-webkit-scrollbar-thumb]:rounded-full overflow-y-hidden overscroll-x-contain snap-x snap-mandatory scroll-smooth"
                role="radiogroup"
                aria-orientation="horizontal"
                aria-label={displayLabel}
                onKeyDown={onRadioGroupKeyDown}>
                {tiles.map((tile) => {
                    const yearLabel = tile.name || tile.value;
                    const isSelected = selectedValue === tile.value || tile.selected;
                    const isDisabled = readOnly || tile.orderable === false;
                    const tabIndex = !isDisabled && tile.value === tabStopValue ? 0 : -1;
                    const tileClassName = cn(
                        'flex w-[110px] shrink-0 flex-col items-center justify-center rounded px-4 py-3 border border-solid body-copy-l-bold text-neutral-10 text-center whitespace-nowrap outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                        isSelected ? 'border-brown-20' : 'border-neutral-80',
                        isDisabled && !isSelected && 'opacity-50 cursor-not-allowed',
                        !isDisabled && 'cursor-pointer'
                    );

                    const assignRef = (node: HTMLElement | null) => {
                        if (isSelected) {
                            selectedRef.current = node;
                        }
                    };

                    if (onSelect) {
                        return (
                            <button
                                key={tile.value}
                                ref={assignRef}
                                type="button"
                                role="radio"
                                aria-checked={isSelected}
                                aria-label={yearLabel}
                                tabIndex={tabIndex}
                                disabled={isDisabled}
                                data-testid={`wine-vintage-${tile.value}`}
                                data-selected={isSelected || undefined}
                                className={tileClassName}
                                onClick={() => {
                                    if (!isDisabled) {
                                        onSelect(tile.value);
                                    }
                                }}>
                                {yearLabel}
                            </button>
                        );
                    }

                    if (isDisabled) {
                        return (
                            <span
                                key={tile.value}
                                ref={assignRef}
                                role="radio"
                                aria-checked={isSelected}
                                aria-disabled="true"
                                aria-label={yearLabel}
                                tabIndex={-1}
                                data-testid={`wine-vintage-${tile.value}`}
                                data-selected={isSelected || undefined}
                                className={tileClassName}>
                                {yearLabel}
                            </span>
                        );
                    }

                    return (
                        <Link
                            key={tile.value}
                            ref={assignRef}
                            to={tile.href}
                            preventScrollReset
                            role="radio"
                            aria-checked={isSelected}
                            aria-label={yearLabel}
                            tabIndex={tabIndex}
                            data-testid={`wine-vintage-${tile.value}`}
                            data-selected={isSelected || undefined}
                            className={tileClassName}>
                            {yearLabel}
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}
